<template>
    <div class="loginpage">
        <img class="loginpage__background">
        <div class="loginpage__content">
            <div class="container">
                <div class="row">
                    <div class="col-sm-10 d-flex align-items-center header">
                        <img class="logo" src="./../assets/images/logo-lely.svg" alt="Lely logo"/>
                        <slot name="header"/>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-10">
                        <div class="loginpage__title-holder">
                            <slot/>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-10">
                        <div class="loginpage__form">
                            <div class="alert alert-info" role="alert" v-if="hasNoAccess">
                                <div class="alert-body">
                                    <i class="lely-icon-warning"></i>
                                    {{ noAccessMessage }}
                                </div>
                            </div>
                            <div class="alert alert-info" role="alert" v-if="userNotFoundError">
                                <div class="alert-body">
                                    <i class="lely-icon-warning"></i>
                                    {{ notFoundMessage }}
                                </div>
                            </div>
                            <form v-if="credentials" @submit.prevent="loginByCredentials()">
                                <div class="form-group">
                                    <label class="text-white" for="email">{{ emailLabel }}</label>
                                    <input id="email" class="form-control" type="email" v-model="email" required
                                           :disabled="loading"
                                           data-testid="email"
                                           autocomplete="username"
                                           autofocus ref="email">
                                </div>
                                <div class="form-group">
                                    <label class="text-white" for="password">{{ passwordLabel }}</label>
                                    <b-input-group>
                                        <input id="password"
                                               class="form-control"
                                               :disabled="loading"
                                               :type="passwordShown ? 'text': 'password'"
                                               v-model="password"
                                               data-testid="password"
                                               autocomplete="current-password"
                                               required>

                                        <template #append>
                                            <b-btn @click="passwordShown = !passwordShown">
                                                <i :class="{'lely-icon-hide': passwordShown, 'lely-icon-show': !passwordShown}"
                                                   class="password-shown-icon m-0"/>
                                            </b-btn>
                                        </template>
                                    </b-input-group>
                                </div>
                                <div class="d-flex justify-content-end">
                                    <button
                                        class="btn btn-primary btn-primary--dark loginpage__btn m-0"
                                        data-testid="submit"
                                        type="submit"
                                        value="submit"
                                        :disabled="loading || loading"
                                    >
                                        <b-spinner v-if="loading" class="mr-2" small/>
                                        {{ submitLabel }}
                                    </button>
                                </div>
                            </form>
                            <form v-if="shownSsoTypes.length" class="my-2">
                                <hr/>
                                <b-spinner v-if="loading" />
                                <div v-else class="d-flex flex-column">
                                    <b-btn v-for="ssoType in shownSsoTypes" :key="ssoType.type" type="submit" class="btn sso mb-2"
                                           @click.prevent="initSso(ssoType.type)">
                                        <img :src="ssoType.icon"/>
                                        {{ ssoType.label }}
                                    </b-btn>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import LelyAuth from '../core/Auth';

@Component({})
export default class LelyLoginView extends Vue {

    @Prop({type: Boolean, default: true})
    sso!: boolean;

    @Prop({type: Boolean, default: false})
    google!: boolean;

    @Prop({type: Boolean, default: false})
    credentials!: boolean;

    @Prop({default: 'E-mail'})
    emailLabel!: string;

    @Prop({default: 'Password'})
    passwordLabel!: string;

    @Prop({default: 'Sign in'})
    submitLabel!: string;

    @Prop({default: 'User not found'})
    notFoundMessage!: string;

    @Prop({default: 'User has no access'})
    noAccessMessage!: string;

    @Prop({default: 'Login for Lely employees'})
    ssoLabel!: string;

    @Prop({default: 'Login via Google'})
    googleLabel!: string;

    email = '';
    password = '';
    userNotFoundError = false;
    hasNoAccess = false;
    loading = false;
    passwordShown = false;

    mounted(): void {
        if (this.credentials) {
            (this.$refs.email as HTMLInputElement).focus();
        }

        if (this.shownSsoTypes.length) {
            if (this.$route.query.access_token) {
                this.loading = true;
                this.loginByToken(String(this.$route.query.access_token));
                this.$route.query.access_token = '';
            } else if (this.$route.query.error) {
                this.loading = false;

                this.$bvModal.msgBoxOk([
                    this.$createElement('h3', `An error occured: ${this.$route.query.error}`),
                    this.$createElement('pre', {
                        attrs: {
                            style: 'white-space: pre-wrap'
                        }
                    }, this.$route.query.error_description),
                ]);
            } else if (this.shownSsoTypes.length === 1) {
                this.initSso(this.shownSsoTypes[0].type);
            } else {
                this.loading = false;
            }
        }
    }

    initSso(type: string): void {
        this.loading = true;
        LelyAuth.initSso(type);
    }

    get shownSsoTypes(): { type: string, label: string, icon: string }[] {
        let ssoTypes = [];

        if (this.sso) {
            ssoTypes.push({
                type: 'sso',
                label: this.ssoLabel,
                icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMyAyMyI+CiAgICA8cGF0aCBmaWxsPSIjZjNmM2YzIiBkPSJNMCAwaDIzdjIzSDB6Ii8+CiAgICA8cGF0aCBmaWxsPSIjZjM1MzI1IiBkPSJNMSAxaDEwdjEwSDF6Ii8+CiAgICA8cGF0aCBmaWxsPSIjODFiYzA2IiBkPSJNMTIgMWgxMHYxMEgxMnoiLz4KICAgIDxwYXRoIGZpbGw9IiMwNWE2ZjAiIGQ9Ik0xIDEyaDEwdjEwSDF6Ii8+CiAgICA8cGF0aCBmaWxsPSIjZmZiYTA4IiBkPSJNMTIgMTJoMTB2MTBIMTJ6Ii8+Cjwvc3ZnPgo=',
            });
        }

        if (this.google) {
            ssoTypes.push({
                type: 'google',
                label: this.googleLabel,
                icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MzMuNSA1NDQuMyI+PHBhdGggZmlsbD0iIzQyODVmNCIgZD0iTTUzMy41IDI3OC40YzAtMTguNS0xLjUtMzcuMS00LjctNTUuM0gyNzIuMXYxMDQuOGgxNDdjLTYuMSAzMy44LTI1LjcgNjMuNy01NC40IDgyLjd2NjhoODcuN2M1MS41LTQ3LjQgODEuMS0xMTcuNCA4MS4xLTIwMC4yeiIvPjxwYXRoIGZpbGw9IiMzNGE4NTMiIGQ9Ik0yNzIuMSA1NDQuM2M3My40IDAgMTM1LjMtMjQuMSAxODAuNC02NS43bC04Ny43LTY4Yy0yNC40IDE2LjYtNTUuOSAyNi05Mi42IDI2LTcxIDAtMTMxLjItNDcuOS0xNTIuOC0xMTIuM0gyOC45djcwLjFjNDYuMiA5MS45IDE0MC4zIDE0OS45IDI0My4yIDE0OS45eiIvPjxwYXRoIGZpbGw9IiNmYmJjMDQiIGQ9Ik0xMTkuMyAzMjQuM2MtMTEuNC0zMy44LTExLjQtNzAuNCAwLTEwNC4yVjE1MEgyOC45Yy0zOC42IDc2LjktMzguNiAxNjcuNSAwIDI0NC40bDkwLjQtNzAuMXoiLz48cGF0aCBmaWxsPSIjZWE0MzM1IiBkPSJNMjcyLjEgMTA3LjdjMzguOC0uNiA3Ni4zIDE0IDEwNC40IDQwLjhsNzcuNy03Ny43QzQwNSAyNC42IDMzOS43LS44IDI3Mi4xIDAgMTY5LjIgMCA3NS4xIDU4IDI4LjkgMTUwbDkwLjQgNzAuMWMyMS41LTY0LjUgODEuOC0xMTIuNCAxNTIuOC0xMTIuNHoiLz48L3N2Zz4=',
            });
        }

        return ssoTypes;
    }

    async loginByCredentials() {
        this.loading = true;

        try {
            const user = await LelyAuth.authenticateByCredentials(this.email, this.password)

            this.$emit('authenticated', {user, redirectTo: this.$route.query.redirect_to});
        } catch (err) {
            this.checkError(err);
        } finally {
            this.loading = false;
        }
    }

    async loginByToken(token: string) {
        try {
            const user = await LelyAuth.authenticateByToken(token);

            this.$emit('authenticated', {user, redirectTo: this.$route.query.redirect_to});
        } catch (err) {
            this.checkError(err);
        } finally {
            this.loading = false;
        }
    }

    checkError(err: any): void {
        if (err.message === 'USER_NO_ACCESS') {
            this.hasNoAccess = true;
        } else {
            this.userNotFoundError = true;
        }
    }
}

</script>

<style lang="scss" scoped>
.loginpage__background {
    width: 100%;
    position: fixed;
    top: 0;
    right: 0;
    background-repeat: no-repeat;
    background-image: url(/images/login.jpg);
    background-size: cover;
}

.header {
    margin-top: 50px;
    margin-bottom: 25px;
}

.logo {
    height: 83px;
    margin-right: 25px;
}

.password-shown-icon:before {
    position: relative;
    top: 2px;
}

.sso {
    align-items: center;
    display: flex;

    img {
        height: 16px;
        transform: none;
        width: 16px;
    }
}
</style>
